import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { GroupService } from '../../../services/group.service';
import { CurrencyPipe } from '@angular/common';
import { DrawerLayoutComponent, DrawerLayoutState } from '../../drawer-layout/drawer-layout.component';
import { DrawerSectionComponent } from '../../drawer-layout/drawer-section.component';
import { DrawerStatComponent } from '../../drawer-layout/drawer-stat.component';
import { TransferBudgetComponent } from '../../../bucket/transfer-budget/transfer-budget.component';
import { SuccessResponse } from '../../../generated-api/models';

@Component({
    selector: 'app-unbudgeted-drawer-wrapper',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './unbudgeted-drawer-wrapper.component.html',
    imports: [
        DrawerLayoutComponent,
        DrawerSectionComponent,
        DrawerStatComponent,
        TransferBudgetComponent,
        CurrencyPipe,
    ],
})
export class UnbudgetedDrawerWrapperComponent {
  public readonly groupService = inject(GroupService);
  readonly categoryPageQuery = injectQuery(() => this.groupService.groupPageQueryOptions());
  readonly layoutState = computed<DrawerLayoutState>(() => {
    if (this.categoryPageQuery.data()) return 'ready';
    return this.categoryPageQuery.isError() ? 'error' : 'loading';
  });

  readonly balanceBreakdown = computed(() => {
    const balance = this.categoryPageQuery.data()?.unbudgeted;
    if (!balance) return [];

    const monthlyPlanRemaining = balance.monthlyBudgetRemaining ?? 0;

    // Signed contributions match the server's Available Balance reconciliation.
    const rows = [
      { label: 'Net account balances', amount: balance.summedAccounts, hint: 'Included account values, minus credit cards and loans.' },
      { label: 'Budget balances', amount: -balance.summedBucketBalances, hint: 'Subtracts accumulated income, spending, and savings balances.' },
      { label: 'Pending transactions', amount: balance.summedPendingTransactions },
      { label: 'Uncategorized transactions', amount: -(balance.summedUnsortedTransactions ?? 0) },
      {
        label: 'Unbudgeted amount',
        amount: -monthlyPlanRemaining,
        hint: 'Planned income not yet assigned to this month’s budgets.',
      },
    ];
    // The API rounds each field separately from its authoritative total.
    const displayedCents = rows.reduce((sum, row) => sum + Math.round(row.amount * 100), 0);
    const roundingCents = Math.round(balance.unbudgeted * 100) - displayedCents;
    if (roundingCents !== 0) {
      rows.push({
        label: balance.summedUnsortedTransactions == null || balance.monthlyBudgetRemaining == null
          ? 'Other adjustments'
          : 'Rounding adjustment',
        amount: roundingCents / 100,
      });
    }
    return rows.filter(row => row.amount !== 0);
  });

  handleTransferSuccess(_event: SuccessResponse): void {
    this.groupService.invalidateCurrentGroupPage();
  }

  getInitialAmount(groupPage: { unbudgeted?: { unbudgeted?: number } }) {
    return Math.abs(groupPage.unbudgeted?.unbudgeted ?? 0);
  }
}
